import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import ViewCustomerInvoiceDialog from "./ViewCustomerInvoiceDialog";
import ViewExciseClaimDialog from "./ViewExciseClaimDialog";
import ViewClaimDialog from "./ViewClaimDialog";
import {
  formatPayoutDateForDisplay,
  formatPayoutDateForInput,
  isPayoutDateOnOrAfterCreationDate,
  parsePayoutDateInput,
} from "../utils/preFinanceDateUtils";

const formatMoney = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return value.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPeriod = (dossier) => {
  if (!dossier?.year || !dossier?.startMonth || !dossier?.endMonth) {
    return "—";
  }

  const months =
    dossier.startMonth === dossier.endMonth
      ? String(dossier.startMonth).padStart(2, "0")
      : `${String(dossier.startMonth).padStart(2, "0")}–${String(dossier.endMonth).padStart(2, "0")}`;
  const periodType = dossier.periodType === 2 ? "p/Q" : "p/M";
  return `${dossier.year} ${months} ${periodType}`;
};

const StackField = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography>{label}:</Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography>{value ?? "—"}</Typography>
    </Grid>
  </>
);

const SummaryPanelRow = ({ label, value }) => (
  <Grid container sx={{ mb: 0.25 }}>
    <Grid item xs={8}>
      <Typography sx={{ fontSize: 18, color: "white" }}>{label}:</Typography>
    </Grid>
    <Grid item xs={4}>
      <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>{value}</Typography>
    </Grid>
  </Grid>
);

const GRID_ROW_HEIGHT = 52;
const GRID_CHROME_HEIGHT = 143;
const PAGE_SIZE_STEP = 5;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 100;

const measureAvailableRowArea = (container) => {
  const containerHeight = container.getBoundingClientRect().height;
  if (containerHeight < 120) {
    return 0;
  }

  const gridRoot = container.querySelector(".MuiDataGrid-root");
  if (!gridRoot) {
    return Math.max(0, containerHeight - GRID_CHROME_HEIGHT);
  }

  const toolbar = gridRoot.querySelector(".MuiDataGrid-toolbarContainer");
  const columnHeaders = gridRoot.querySelector(".MuiDataGrid-columnHeaders");
  const footer = gridRoot.querySelector(".MuiDataGrid-footerContainer");
  const chromeHeight =
    (toolbar?.offsetHeight || 0) + (columnHeaders?.offsetHeight || 0) + (footer?.offsetHeight || 0);
  const gridHeight = gridRoot.getBoundingClientRect().height || containerHeight;

  // Never use virtual scroller height: with pagination it shrinks to the current page size.
  return Math.max(0, gridHeight - chromeHeight);
};

const measureRowHeight = (gridRoot) => {
  if (!gridRoot) {
    return GRID_ROW_HEIGHT;
  }

  const renderedRow = gridRoot.querySelector(".MuiDataGrid-row");
  if (renderedRow?.offsetHeight > 0) {
    return renderedRow.offsetHeight;
  }

  const cssRowHeight = window.getComputedStyle(gridRoot).getPropertyValue("--DataGrid-rowHeight");
  const parsedRowHeight = Number.parseFloat(cssRowHeight);
  return Number.isFinite(parsedRowHeight) && parsedRowHeight > 0 ? parsedRowHeight : GRID_ROW_HEIGHT;
};

const calculatePageSizeForHeight = (rowAreaHeight, rowHeight = GRID_ROW_HEIGHT) => {
  if (rowAreaHeight <= 0 || rowHeight <= 0) {
    return MIN_PAGE_SIZE;
  }

  const rawCount = Math.floor(rowAreaHeight / rowHeight);
  const steppedCount = Math.floor(rawCount / PAGE_SIZE_STEP) * PAGE_SIZE_STEP;
  return Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, steppedCount));
};

const buildRowsPerPageOptions = (optimalPageSize) => {
  const upperBound = Math.min(
    MAX_PAGE_SIZE,
    Math.max(optimalPageSize + PAGE_SIZE_STEP * 4, PAGE_SIZE_STEP * 6)
  );
  const options = new Set([optimalPageSize]);

  for (let size = PAGE_SIZE_STEP; size <= upperBound; size += PAGE_SIZE_STEP) {
    options.add(size);
  }

  return [...options].sort((a, b) => a - b);
};

const formatPayoutAmountForInput = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "";
  }

  return Number(value).toFixed(2).replace(".", ",");
};

const parsePayoutAmountInput = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.round((parsed + Number.EPSILON) * 100) / 100;
};

const PreFinanceDossierDetailDialog = ({
  dossier,
  open,
  onClose,
  customerId,
  customerNumber,
  canFinalizePreFinance = false,
  canUnfinalizePreFinance = false,
  onRequestDelete,
  onFinalized,
  onUnfinalized,
  onPayoutAmountAdjusted,
  isDeleting = false,
}) => {
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [finalizeError, setFinalizeError] = useState("");
  const [payoutAmountError, setPayoutAmountError] = useState("");
  const [showPayoutReasonRequired, setShowPayoutReasonRequired] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isUnfinalizing, setIsUnfinalizing] = useState(false);
  const [isAdjustingPayoutAmount, setIsAdjustingPayoutAmount] = useState(false);
  const [isEditingPayoutAmount, setIsEditingPayoutAmount] = useState(false);
  const [detailPayoutAmount, setDetailPayoutAmount] = useState("");
  const [detailPayoutAdjustmentReason, setDetailPayoutAdjustmentReason] = useState("");
  const [detailPayoutDate, setDetailPayoutDate] = useState(formatPayoutDateForInput());
  const [pageSize, setPageSize] = useState(10);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([5, 10, 15, 20]);
  const gridContainerRef = useRef(null);
  const [countryData, setCountryData] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [invoiceStateData, setInvoiceStateData] = useState([]);
  const [invoiceLabelData, setInvoiceLabelData] = useState([]);
  const [exciseCountryData, setExciseCountryData] = useState([]);
  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    downloadFile: true,
    title: "",
    params: null,
    custInfo: [],
  });
  const [viewExciseClaimDialog, setViewExciseClaimDialog] = useState({
    isOpen: false,
    title: "",
    params: null,
    countryData: [],
    downloadFile: true,
    custInfo: [],
  });
  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    params: null,
    custInfo: [],
    subTitle: "",
  });

  const isVatDossier = dossier?.preFinanceType === 0;

  useEffect(() => {
    if (!open || !dossier) {
      return;
    }

    setDetailPayoutDate(
      dossier.isFinalized && dossier.payoutDate
        ? formatPayoutDateForInput(dossier.payoutDate)
        : formatPayoutDateForInput()
    );
    setDetailPayoutAmount(formatPayoutAmountForInput(dossier.payoutAmount));
    setDetailPayoutAdjustmentReason(dossier.payoutAdjustmentReason || "");
    setIsEditingPayoutAmount(false);
    setFinalizeError("");
    setPayoutAmountError("");
    setShowPayoutReasonRequired(false);
  }, [open, dossier]);

  const syncGridPageSize = useCallback(() => {
    const container = gridContainerRef.current;
    if (!container || !open) {
      return;
    }

    const containerHeight = container.getBoundingClientRect().height;
    if (containerHeight < 120) {
      return;
    }

    const gridRoot = container.querySelector(".MuiDataGrid-root");
    const rowAreaHeight = measureAvailableRowArea(container);
    const rowHeight = measureRowHeight(gridRoot);
    const optimalPageSize = calculatePageSizeForHeight(rowAreaHeight, rowHeight);

    setPageSize((currentPageSize) => (currentPageSize === optimalPageSize ? currentPageSize : optimalPageSize));
    setRowsPerPageOptions(buildRowsPerPageOptions(optimalPageSize));
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let frameId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        syncGridPageSize();
        frameId = requestAnimationFrame(syncGridPageSize);
      });
    };

    scheduleSync();

    const container = gridContainerRef.current;
    const observer = container ? new ResizeObserver(scheduleSync) : null;
    if (container && observer) {
      observer.observe(container);
    }

    window.addEventListener("resize", scheduleSync);

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleSync);
    };
  }, [open, isLoading, syncGridPageSize]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchLookupData = async () => {
      try {
        if (isVatDossier) {
          const [countries, suppliers, currencies, products, invoiceStates, invoiceLabels] = await Promise.all([
            axios.get("/api/v1/countries", { signal }),
            axios.get("/api/v1/suppliers", { signal }),
            axios.get("/api/v1/currencies", { signal }),
            axios.get("/api/v1/productTypes", { signal }),
            axios.get("/api/v1/invoiceStates", { signal }),
            axios.get("/api/v1/invoiceLabels", { signal }),
          ]);
          setCountryData(countries.data || []);
          setSupplierData(suppliers.data || []);
          setCurrencyData(currencies.data || []);
          setProductData(products.data || []);
          setInvoiceStateData(invoiceStates.data || []);
          setInvoiceLabelData(invoiceLabels.data || []);
        } else {
          const response = await axios.get("/api/v1/exciseCountries", { signal });
          setExciseCountryData(response.data || []);
        }
      } catch (requestError) {
        if (requestError.code !== "ERR_CANCELED") {
          console.error("Unable to load prefinance detail lookup data:", requestError);
        }
      }
    };

    fetchLookupData();
    return () => controller.abort();
  }, [open, isVatDossier]);

  const handleRefreshSources = useCallback(async () => {
    if (!dossier?.preFinanceId) {
      return;
    }

    const endpoint = isVatDossier
      ? `/api/v1/customerInvoicesPrefinanced/${dossier.preFinanceId}`
      : `/api/v1/exciseClaimsByPreFinanceId/${dossier.preFinanceId}`;
    const response = await axios.get(endpoint);
    setSources(response.data || []);
  }, [dossier?.preFinanceId, isVatDossier]);

  useEffect(() => {
    if (!open || !dossier?.preFinanceId) {
      setSources([]);
      setError("");
      return;
    }

    const controller = new AbortController();
    const handleFetchSources = async () => {
      setIsLoading(true);
      setError("");

      try {
        const endpoint = isVatDossier
          ? `/api/v1/customerInvoicesPrefinanced/${dossier.preFinanceId}`
          : `/api/v1/exciseClaimsByPreFinanceId/${dossier.preFinanceId}`;
        const response = await axios.get(endpoint, { signal: controller.signal });
        setSources(response.data || []);
      } catch (requestError) {
        if (requestError.code !== "ERR_CANCELED") {
          console.error("Unable to load prefinance dossier sources:", requestError);
          setError("The linked source records could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    handleFetchSources();
    return () => controller.abort();
  }, [dossier, open, isVatDossier]);

  const vatTotals = useMemo(() => {
    if (!isVatDossier) {
      return {
        invoiceCount: 0,
        missingCopies: 0,
      };
    }

    return sources.reduce(
      (acc, row) => ({
        invoiceCount: acc.invoiceCount + 1,
        missingCopies: acc.missingCopies + (row.fileId > 0 ? 0 : 1),
      }),
      {
        invoiceCount: 0,
        missingCopies: 0,
      }
    );
  }, [isVatDossier, sources]);

  const exciseTotals = useMemo(() => {
    if (isVatDossier) {
      return {
        declarationCount: 0,
        totalLiters: 0,
        totalEur: 0,
        totalLocal: 0,
      };
    }

    return sources.reduce(
      (acc, row) => ({
        declarationCount: acc.declarationCount + 1,
        totalLiters: acc.totalLiters + (row.liters || 0),
        totalEur: acc.totalEur + (row.claimAmountEUR || 0),
        totalLocal: acc.totalLocal + (row.claimAmountLocal || 0),
      }),
      {
        declarationCount: 0,
        totalLiters: 0,
        totalEur: 0,
        totalLocal: 0,
      }
    );
  }, [isVatDossier, sources]);

  const handleOpenInvoiceView = useCallback(
    (row) => {
      setViewCustomerInvoiceDialog({
        isOpen: true,
        downloadFile: true,
        title: "Manage Invoice",
        params: row,
        custInfo: [{ customerId, customerNumber }],
        productInfo: [
          {
            productCode: row.claimProductCode,
            productSubCode: row.claimProductSubCode,
          },
        ],
        subTitle: `View invoice ${row.invoiceNumber}`,
        handleFile: () => {},
      });
    },
    [customerId, customerNumber]
  );

  const handleOpenExciseView = useCallback(
    (row) => {
      setViewExciseClaimDialog({
        isOpen: true,
        title: "View Excise Claim",
        params: row,
        countryData: exciseCountryData,
        downloadFile: true,
        custInfo: [{ customerId, customerNumber }],
        subTitle: `View details for ${row.claimCode}`,
      });
    },
    [customerId, customerNumber, exciseCountryData]
  );

  const handleOpenLinkedClaim = useCallback(async () => {
    if (!dossier?.claimId) {
      return;
    }

    if (isVatDossier) {
      setViewClaimDialog({
        isOpen: true,
        title: "View Claim",
        params: { claimId: dossier.claimId },
        custInfo: [{ customerId, customerNumber }],
        subTitle: `View details for ${dossier.claimCode || dossier.claimId}`,
      });
      return;
    }

    try {
      const response = await axios.get(`/api/v1/exciseClaims/${dossier.claimId}`);
      const claim = response.data;
      setViewExciseClaimDialog({
        isOpen: true,
        title: "View Excise Claim",
        params: claim,
        countryData: exciseCountryData,
        downloadFile: true,
        custInfo: [{ customerId, customerNumber }],
        subTitle: `View details for ${claim.claimCode || dossier.claimCode || dossier.claimId}`,
      });
    } catch (requestError) {
      console.error("Unable to load linked Excise claim:", requestError);
      setError(
        requestError.response?.data?.message ||
          `Unable to load Excise claim ${dossier.claimCode || dossier.claimId}.`
      );
    }
  }, [customerId, customerNumber, dossier, exciseCountryData, isVatDossier]);

  const getSupplierName = useCallback(
    (supplierId) => supplierData.find((supplier) => supplier.supplierId === supplierId)?.supplierName || supplierId || "—",
    [supplierData]
  );

  const getCountryCode = useCallback(
    (countryId) => countryData.find((country) => country.countryId === countryId)?.countryCode || countryId || "—",
    [countryData]
  );

  const getCurrencyCode = useCallback(
    (currencyId) => currencyData.find((currency) => currency.currencyId === currencyId)?.isoCode || currencyId || "—",
    [currencyData]
  );

  const getInvoiceStateLabel = useCallback(
    (invoiceStateId) =>
      invoiceStateData.find((state) => state.invoiceStateId === invoiceStateId)?.invoiceStateShortCode ||
      invoiceStateId ||
      "—",
    [invoiceStateData]
  );

  const columns = useMemo(() => {
    if (isVatDossier) {
      return [
        { field: "invoiceNumber", headerName: "Invoice Number", flex: 0.7, minWidth: 140 },
        {
          field: "invoiceDate",
          headerName: "Invoice Date",
          width: 120,
          valueFormatter: ({ value }) => (value ? moment(value).format("DD-MM-YYYY") : "—"),
        },
        {
          field: "supplierId",
          headerName: "Supplier",
          flex: 1,
          minWidth: 160,
          valueGetter: ({ row }) => getSupplierName(row.supplierId),
        },
        {
          field: "countryCode",
          headerName: "Country",
          width: 90,
          valueGetter: ({ row }) => getCountryCode(row.countryCode),
        },
        {
          field: "currencyId",
          headerName: "Currency",
          width: 90,
          valueGetter: ({ row }) => getCurrencyCode(row.currencyId),
        },
        {
          field: "invoiceGrossAmount",
          headerName: "Net Amount",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: ({ value }) => formatMoney(value),
        },
        {
          field: "invoiceVATAmountLocalCurrency",
          headerName: "Local VAT",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: ({ value }) => formatMoney(value),
        },
        {
          field: "fileId",
          headerName: "Invoice PDF available",
          width: 150,
          renderCell: ({ value }) =>
            value > 0 ? (
              <CheckCircleOutlineIcon sx={{ color: "green", margin: "0 auto" }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ color: "red", margin: "0 auto" }} />
            ),
        },
        {
          field: "invoiceStateId",
          headerName: "Invoice State",
          width: 110,
          valueGetter: ({ row }) => getInvoiceStateLabel(row.invoiceStateId),
        },
        {
          field: "actions",
          headerName: "Actions",
          width: 90,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <IconButton
              aria-label={`View invoice ${row.invoiceNumber}`}
              onClick={() => handleOpenInvoiceView(row)}
            >
              <VisibilityIcon />
            </IconButton>
          ),
        },
      ];
    }

    return [
      { field: "claimCode", headerName: "Declaration", flex: 1, minWidth: 160 },
      { field: "year", headerName: "Year", width: 80 },
      {
        field: "period",
        headerName: "Period",
        width: 100,
        valueGetter: ({ row }) =>
          row.startMonth === row.endMonth
            ? String(row.startMonth).padStart(2, "0")
            : `${row.startMonth}–${row.endMonth}`,
      },
      {
        field: "liters",
        headerName: "Liters",
        width: 110,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "claimAmountEUR",
        headerName: "Amount EUR",
        width: 125,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "claimAmountLocal",
        headerName: "Amount local",
        width: 125,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton aria-label={`View declaration ${row.claimCode}`} onClick={() => handleOpenExciseView(row)}>
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ];
  }, [
    getCountryCode,
    getCurrencyCode,
    getInvoiceStateLabel,
    getSupplierName,
    handleOpenExciseView,
    handleOpenInvoiceView,
    isVatDossier,
  ]);

  const sourceRowId = (row) => (isVatDossier ? row.customerInvoiceId : row.claimId);

  const countryLabel = dossier?.countryName || dossier?.countryCode || "—";
  const claimStateLabel = dossier?.claimStateDescription || dossier?.claimStateShortCode || "—";
  const payoutDateLabel = formatPayoutDateForDisplay(dossier?.payoutDate);
  const finalizedLabel = dossier?.isFinalized ? "Yes" : "No";
  const canManagePayout = canFinalizePreFinance;
  const canFinalizeDossier =
    canManagePayout &&
    dossier &&
    !dossier.isFinalized &&
    !dossier.isLegacy &&
    Boolean(parsePayoutDateInput(detailPayoutDate)) &&
    isPayoutDateOnOrAfterCreationDate(detailPayoutDate, dossier.creationDate) &&
    !isFinalizing &&
    !isUnfinalizing &&
    !isDeleting;
  const canUnfinalizeDossier =
    canUnfinalizePreFinance &&
    dossier &&
    dossier.isFinalized &&
    !dossier.isLegacy &&
    !isUnfinalizing &&
    !isFinalizing &&
    !isDeleting;
  const showOpenPayoutEditor =
    dossier && !dossier.isLegacy && !dossier.isFinalized && canManagePayout;
  const showFinalizedPayoutUndo =
    dossier && !dossier.isLegacy && dossier.isFinalized && canUnfinalizePreFinance;
  const canAdjustPayoutAmount =
    showOpenPayoutEditor &&
    !isAdjustingPayoutAmount &&
    !isFinalizing &&
    !isUnfinalizing &&
    !isDeleting;

  const calculatedPayoutAmount =
    dossier?.calculatedPayoutAmount ?? dossier?.payoutAmount ?? null;
  const hasAdjustedPayoutAmount =
    dossier?.calculatedPayoutAmount != null &&
    dossier?.payoutAmount != null &&
    Number(dossier.calculatedPayoutAmount) !== Number(dossier.payoutAmount);

  const handleStartAdjustPayoutAmount = () => {
    if (!canAdjustPayoutAmount) {
      return;
    }

    setDetailPayoutAmount(formatPayoutAmountForInput(dossier?.payoutAmount));
    setDetailPayoutAdjustmentReason(dossier?.payoutAdjustmentReason || "");
    setPayoutAmountError("");
    setShowPayoutReasonRequired(false);
    setIsEditingPayoutAmount(true);
  };

  const handleCancelAdjustPayoutAmount = () => {
    setDetailPayoutAmount(formatPayoutAmountForInput(dossier?.payoutAmount));
    setDetailPayoutAdjustmentReason(dossier?.payoutAdjustmentReason || "");
    setPayoutAmountError("");
    setShowPayoutReasonRequired(false);
    setIsEditingPayoutAmount(false);
  };

  const handleSavePayoutAmount = async () => {
    if (
      !dossier?.preFinanceId ||
      !showOpenPayoutEditor ||
      !isEditingPayoutAmount ||
      isAdjustingPayoutAmount ||
      isFinalizing ||
      isUnfinalizing ||
      isDeleting
    ) {
      return;
    }

    const parsedAmount = parsePayoutAmountInput(detailPayoutAmount);
    if (parsedAmount === null || parsedAmount <= 0.01) {
      setShowPayoutReasonRequired(false);
      setPayoutAmountError("Enter a valid payout amount greater than 0.01.");
      return;
    }

    const reason = detailPayoutAdjustmentReason.trim();
    if (reason.length < 3) {
      setPayoutAmountError("");
      setShowPayoutReasonRequired(true);
      return;
    }

    setIsAdjustingPayoutAmount(true);
    setPayoutAmountError("");
    setShowPayoutReasonRequired(false);

    try {
      const response = await axios.post("/api/v1/preFinance/adjustPayoutAmount", {
        preFinanceId: dossier.preFinanceId,
        payoutAmount: parsedAmount,
        reason,
      });
      setIsEditingPayoutAmount(false);
      onPayoutAmountAdjusted?.(response.data);
    } catch (requestError) {
      console.error("Unable to adjust prefinance payout amount:", requestError);
      setPayoutAmountError(
        requestError.response?.data?.message ||
          "The payout amount could not be updated."
      );
    } finally {
      setIsAdjustingPayoutAmount(false);
    }
  };

  const handleFinalizeDossier = async () => {
    if (!dossier?.preFinanceId || !canFinalizeDossier) {
      return;
    }

    const parsedPayoutDate = parsePayoutDateInput(detailPayoutDate);
    if (!parsedPayoutDate) {
      setFinalizeError("Enter a valid payout date in DD-MM-YYYY format.");
      return;
    }

    if (!isPayoutDateOnOrAfterCreationDate(detailPayoutDate, dossier.creationDate)) {
      setFinalizeError("The payout date cannot be earlier than the dossier creation date.");
      return;
    }

    setIsFinalizing(true);
    setFinalizeError("");

    try {
      const response = await axios.post("/api/v1/preFinance/finalize", {
        entityId: Number(customerId),
        payoutDate: parsedPayoutDate,
        preFinanceIds: [dossier.preFinanceId],
      });
      onFinalized?.(response.data);
    } catch (requestError) {
      console.error("Unable to finalize prefinance dossier:", requestError);
      setFinalizeError(
        requestError.response?.data?.message ||
          "The prefinance dossier could not be finalized."
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleUnfinalizeDossier = async () => {
    if (!dossier?.preFinanceId || !canUnfinalizeDossier) {
      return;
    }

    setIsUnfinalizing(true);
    setFinalizeError("");

    try {
      const response = await axios.post("/api/v1/preFinance/unfinalize", {
        entityId: Number(customerId),
        preFinanceIds: [dossier.preFinanceId],
      });
      onUnfinalized?.(response.data);
    } catch (requestError) {
      console.error("Unable to reopen prefinance dossier:", requestError);
      setFinalizeError(
        requestError.response?.data?.message ||
          "The prefinance dossier could not be reopened."
      );
    } finally {
      setIsUnfinalizing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">View pre-finance dossier</Typography>
            {dossier?.isLegacy && <Chip label="Legacy" color="warning" size="small" />}
          </Box>
          <IconButton aria-label="Close dossier details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          flex: "1 1 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
        {dossier?.isLegacy && dossier?.legacyMismatchReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {dossier.legacyMismatchReason}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <StackField label="Product" value={dossier?.productLabel} />
              <StackField label="Country" value={countryLabel} />
              <StackField label="Period" value={formatPeriod(dossier)} />
              <StackField label="Version" value={dossier?.version ?? "—"} />
              {dossier?.claimId ? (
                <>
                  <Grid item xs={5}>
                    <Typography>Claim:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Button
                      type="button"
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={handleOpenLinkedClaim}
                      aria-label={`View linked claim ${dossier.claimCode || dossier.claimId}`}
                      sx={{
                        minWidth: "auto",
                        px: 0,
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        justifyContent: "flex-start",
                      }}
                    >
                      {dossier.claimCode || `#${dossier.claimId}`}
                    </Button>
                  </Grid>
                </>
              ) : (
                <StackField label="Claim" value="—" />
              )}
              <StackField label="Claim state" value={claimStateLabel} />
              <StackField label="Refund amount" value={formatMoney(dossier?.refundAmount)} />
              {(hasAdjustedPayoutAmount || isEditingPayoutAmount) && (
                <StackField
                  label="Calculated payout"
                  value={formatMoney(calculatedPayoutAmount)}
                />
              )}
              {showOpenPayoutEditor && isEditingPayoutAmount ? (
                <>
                  <Grid item xs={5}>
                    <Typography>Payout amount:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Box display="flex" flexDirection="column" gap={1.25}>
                      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        <TextField
                          size="small"
                          value={detailPayoutAmount}
                          onChange={(event) => setDetailPayoutAmount(event.target.value)}
                          disabled={isAdjustingPayoutAmount || isFinalizing || isUnfinalizing || isDeleting}
                          sx={{ width: 140 }}
                          inputProps={{ "aria-label": "Payout amount", inputMode: "decimal" }}
                        />
                        <Button
                          type="button"
                          variant="text"
                          color="success"
                          size="small"
                          onClick={handleSavePayoutAmount}
                          disabled={isAdjustingPayoutAmount || isFinalizing || isUnfinalizing || isDeleting}
                          aria-label="Save payout amount"
                          sx={{
                            minWidth: "auto",
                            px: 0.5,
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            "&:hover": {
                              textDecoration: "underline",
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          {isAdjustingPayoutAmount ? (
                            <CircularProgress size={16} color="success" />
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="text"
                          color="inherit"
                          size="small"
                          onClick={handleCancelAdjustPayoutAmount}
                          disabled={isAdjustingPayoutAmount}
                          aria-label="Cancel payout amount adjustment"
                          sx={{
                            minWidth: "auto",
                            px: 0.5,
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                            "&:hover": {
                              textDecoration: "underline",
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <TextField
                        size="small"
                        label="Reason"
                        value={detailPayoutAdjustmentReason}
                        onChange={(event) => {
                          setDetailPayoutAdjustmentReason(event.target.value);
                          if (showPayoutReasonRequired && event.target.value.trim().length >= 3) {
                            setShowPayoutReasonRequired(false);
                          }
                        }}
                        disabled={isAdjustingPayoutAmount || isFinalizing || isUnfinalizing || isDeleting}
                        required
                        fullWidth
                        multiline
                        minRows={2}
                        inputProps={{ maxLength: 250, "aria-label": "Payout adjustment reason" }}
                        error={showPayoutReasonRequired}
                        helperText={
                          showPayoutReasonRequired
                            ? "Required when adjusting the payout amount"
                            : undefined
                        }
                        FormHelperTextProps={{ sx: { color: "error.main" } }}
                      />
                      {payoutAmountError && (
                        <Typography variant="caption" color="error">
                          {payoutAmountError}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </>
              ) : showOpenPayoutEditor ? (
                <>
                  <Grid item xs={5}>
                    <Typography>Payout amount:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2">{formatMoney(dossier?.payoutAmount)}</Typography>
                      <Button
                        type="button"
                        variant="text"
                        color="primary"
                        size="small"
                        onClick={handleStartAdjustPayoutAmount}
                        disabled={!canAdjustPayoutAmount}
                        aria-label="Adjust payout amount"
                        sx={{
                          ml: 0.5,
                          minWidth: "auto",
                          px: 0.5,
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Adjust
                      </Button>
                    </Box>
                    {payoutAmountError && (
                      <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                        {payoutAmountError}
                      </Typography>
                    )}
                  </Grid>
                </>
              ) : (
                <StackField label="Payout amount" value={formatMoney(dossier?.payoutAmount)} />
              )}
              {hasAdjustedPayoutAmount && dossier?.payoutAdjustmentReason && (
                <StackField label="Adjustment reason" value={dossier.payoutAdjustmentReason} />
              )}
              {showFinalizedPayoutUndo ? (
                <>
                  <Grid item xs={5}>
                    <Typography>Payout date:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2">{payoutDateLabel}</Typography>
                      <Button
                        type="button"
                        variant="text"
                        color="warning"
                        size="small"
                        onClick={handleUnfinalizeDossier}
                        disabled={!canUnfinalizeDossier}
                        aria-label="Undo payout finalization"
                        sx={{
                          ml: 0.5,
                          minWidth: "auto",
                          px: 0.5,
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        {isUnfinalizing ? (
                          <CircularProgress size={16} color="warning" />
                        ) : (
                          "Undo"
                        )}
                      </Button>
                    </Box>
                    {finalizeError && (
                      <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                        {finalizeError}
                      </Typography>
                    )}
                  </Grid>
                </>
              ) : showOpenPayoutEditor ? (
                <>
                  <Grid item xs={5}>
                    <Typography>Payout date:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        size="small"
                        placeholder="DD-MM-YYYY"
                        value={detailPayoutDate}
                        onChange={(event) => setDetailPayoutDate(event.target.value)}
                        disabled={isFinalizing || isUnfinalizing || isDeleting}
                        sx={{ width: 132 }}
                        inputProps={{ "aria-label": "Payout date" }}
                      />
                      <Button
                        type="button"
                        variant="text"
                        color="success"
                        size="small"
                        onClick={handleFinalizeDossier}
                        disabled={!canFinalizeDossier}
                        aria-label="Confirm payout finalization"
                        sx={{
                          ml: 0.5,
                          minWidth: "auto",
                          px: 0.5,
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        {isFinalizing ? (
                          <CircularProgress size={16} color="success" />
                        ) : (
                          "Confirm"
                        )}
                      </Button>
                    </Box>
                    {finalizeError && (
                      <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                        {finalizeError}
                      </Typography>
                    )}
                  </Grid>
                </>
              ) : (
                <StackField label="Payout date" value={payoutDateLabel} />
              )}
              <StackField label="Finalized" value={finalizedLabel} />
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} sx={{ alignSelf: "flex-start" }}>
            <Box sx={{ p: 2, backgroundColor: "#376fd0" }}>
              {isVatDossier ? (
                <>
                  <SummaryPanelRow label="Invoice Count" value={vatTotals.invoiceCount} />
                  <SummaryPanelRow label="Total Refund Amount" value={formatMoney(dossier?.refundAmount)} />
                  <SummaryPanelRow label="Total Payout Amount" value={formatMoney(dossier?.payoutAmount)} />
                  <SummaryPanelRow label="Total Costs" value={formatMoney(dossier?.totalCosts)} />
                  <SummaryPanelRow label="Missing Invoice Copies" value={vatTotals.missingCopies} />
                </>
              ) : (
                <>
                  <SummaryPanelRow label="Declaration Count" value={exciseTotals.declarationCount} />
                  <SummaryPanelRow label="Total Liters" value={formatMoney(exciseTotals.totalLiters)} />
                  <SummaryPanelRow label="Total Amount EUR" value={formatMoney(exciseTotals.totalEur)} />
                  <SummaryPanelRow label="Total Amount Local" value={formatMoney(exciseTotals.totalLocal)} />
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        <Typography variant="subtitle1" gutterBottom>
          {isVatDossier ? "Linked invoices" : "Linked Excise declarations"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isVatDossier && viewCustomerInvoiceDialog.isOpen && (
          <ViewCustomerInvoiceDialog
            key={viewCustomerInvoiceDialog.params?.customerInvoiceId || Date.now()}
            viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
            setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
            countryArr={countryData}
            supplierArr={supplierData}
            currencyArr={currencyData}
            productTypeArr={productData}
            invoiceStateArr={invoiceStateData}
            invoiceLabelArr={invoiceLabelData}
            refreshGrid={handleRefreshSources}
          />
        )}

        {!isVatDossier && viewExciseClaimDialog.isOpen && (
          <ViewExciseClaimDialog
            viewExciseClaimDialog={viewExciseClaimDialog}
            setViewExciseClaimDialog={setViewExciseClaimDialog}
            refreshGrid={handleRefreshSources}
          />
        )}

        {viewClaimDialog.isOpen && (
          <ViewClaimDialog
            viewClaimDialog={viewClaimDialog}
            setViewClaimDialog={setViewClaimDialog}
          />
        )}
        </Box>

        <Box
          ref={gridContainerRef}
          sx={{ flex: 1, minHeight: 240, height: 0, display: "flex", flexDirection: "column" }}
        >
          {isLoading ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 240,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={sources}
              columns={columns}
              getRowId={sourceRowId}
              disableSelectionOnClick
              density="compact"
              pagination
              paginationMode="client"
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={rowsPerPageOptions}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              sx={{ height: "100%", minHeight: 240, width: "100%" }}
            />
          )}
        </Box>

        <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, flexShrink: 0 }}>
          <Grid item>
            <Button type="button" variant="contained" color="primary" onClick={onClose}>
              Close
            </Button>
          </Grid>
          <Grid item display="flex" gap={1}>
            <Button
              type="button"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onRequestDelete?.(dossier)}
              disabled={!canFinalizePreFinance || !dossier || dossier.isFinalized || isDeleting || isFinalizing || isUnfinalizing}
            >
              Delete
              {isDeleting && <CircularProgress size={20} sx={{ ml: 1.25, color: "white" }} />}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PreFinanceDossierDetailDialog;
